<script lang="ts">
	import PreviewImageSelector from './PreviewImage/PreviewImageSelector.svelte';
	import type { PostParent } from '$lib/api/restApi';
	import type { Readable, Writable } from 'svelte/store';
	import CollectionSelector from '$lib/components/CollectionSelector/CollectionSelector.svelte';
	import TagInput from '$lib/components/TagInput/TagInput.svelte';
	import Button from '$lib/components/Button/Button.svelte';
	import { page } from '$app/stores';
	import { atLeastCollaborator } from '$lib/utils/roles';
	import { getHubStores } from '$lib/stores/hubStores';
	import { _ } from 'svelte-i18n';
	import ScheduleTimeDatePicker from '$lib/components/DatePicker/ScheduleTimeDatePicker.svelte';
	import type { Post } from '$lib/api/rpc';
	import { isComposerOpen } from '$textEditor/PostComposer/composerStore';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { editorStoreToPost } from '$textEditor/utils/editorToPost';
	import type { Editor } from 'svelte-tiptap';
	import { isWidget } from '$lib/components/WidgetModal/widgetHelpers';
	import { goto } from '$app/navigation';

	const { hub, posts, restApi } = getHubStores();

	const origin = `${$page.url.origin}/${$page.params.hubPrefix}`;
	const atLeastColl = atLeastCollaborator($hub.user_context);
	const isMember = !atLeastColl;

	export let post: Writable<PostParent>;
	export let editor: Readable<Editor> | undefined = undefined;
	export let beforeSave: (() => Promise<void>) | undefined = undefined;
	export let afterSave: (() => Promise<void>) | undefined = undefined;
	export let showPreviewImg = true;

	let shouldBeScheduled = $post.status == 'scheduled';
	let scheduleTime: Date;
	if (shouldBeScheduled && $post.published_at) {
		scheduleTime = new Date($post.published_at);
	}

	const isWidgetPost = isWidget($post.type);

	const getSubmitButtonLabel = () => {
		if (shouldBeScheduled) return $_('compose.schedule');
		switch ($post.status) {
			case undefined:
				return $_('compose.post');
			case 'draft':
				return atLeastColl ? $_('hubs.management.publish.draft') : $_('compose.submit.draft');
			case 'in-review':
				return atLeastColl ? $_('post.publish') : $_('compose.update');
			default:
				return $_('compose.update');
		}
	};

	const getDisplayType = (post: PostParent) => {
		switch (post.status) {
			case 'draft':
				return 'draft';
			default:
				return 'post';
		}
	};

	const submitDraft = async () => {
		if (isMember) {
			let resp = await restApi.submitDraft($post, $hub.id);
			if (resp.data) {
				toastMessages.success($_('draft.submit.success.alert'));
				$isComposerOpen = false;
				goto(`${origin}/content/published`);
			} else {
				toastMessages.danger($_('draft.submit.error.alert'), resp.error);
			}
		} else {
			let resp = await restApi.publishDraft($post, $hub.id);
			if (resp.data) {
				toastMessages.success($_('draft.publish.success.alert'));
				$isComposerOpen = false;
				goto(`${origin}/content/published`);
			} else {
				toastMessages.danger($_('draft.publish.failure.alert'), resp.error);
			}
		}
	};

	const approveSubmission = async () => {
		try {
			await restApi.approveSubmission($post, $hub.id);
			toastMessages.success($_('submission.publish.success.alert'));
			goto(`/${$page.params.hubPrefix}/content/published`);
			$isComposerOpen = false;
		} catch (e) {
			toastMessages.danger($_('submission.publish.error.alert'), e);
		}
	};

	const save = async ({ asDraft }: { asDraft?: boolean }) => {
		if (beforeSave) {
			try {
				await beforeSave();
			} catch (e) {
				return;
			}
		}
		const initPostStatus = $post.status;

		if ($editor) {
			$post = editorStoreToPost($editor, $post);
		}

		if ((initPostStatus === 'draft' || initPostStatus === 'in-review') && !asDraft) {
			// We must save first before publishing, due to the fact the publish api takes new edits from the
			// draft post stored in the DB.
			const resp = await restApi.savePost($post, $hub.id);

			if (resp.data) {
				switch (initPostStatus) {
					case 'draft':
						void submitDraft();
						break;
					case 'in-review':
						void approveSubmission();
						break;
				}
			} else {
				toastMessages.danger(
					$_('post.save.error.alert', { values: { post: getDisplayType($post) } }),
					resp.error
				);
			}
		} else {
			if (shouldBeScheduled) {
				$post.status = 'scheduled';
			} else if (isMember) {
				$post.status = 'in-review';
			} else if (asDraft) {
				$post.status = 'draft';
			}
			if (shouldBeScheduled) {
				$post.published_at = scheduleTime.toISOString();
			}

			const resp = await restApi.savePost($post, $hub.id);
			if (resp.data) {
				toastMessages.success(
					$_('post.save.success.alert', { values: { post: getDisplayType($post) } })
				);
				// If the post is new, add it to the top of the list
				if (!$post.id && $post.status === undefined) {
					$posts.unshift(resp.data);
				} else {
					// If the post is edited, replace the old post with the new one
					const editedPostIndex = $posts.findIndex((p) => p.id === $post.id);
					if (editedPostIndex !== -1) {
						$posts[editedPostIndex] = resp.data;
					}
				}
				$posts = $posts;
				$isComposerOpen = false;
				if (afterSave) {
					await afterSave();
				}
			} else {
				$post.status = initPostStatus;
				toastMessages.danger(
					$_('post.save.error.alert', { values: { post: getDisplayType($post) } }),
					resp.error
				);
			}
		}
	};

	const unschedule = async () => {
		let resp = await restApi.unSchedulePost($post as unknown as Post);
		if (resp.data) {
			$post.status = 'draft';
			$isComposerOpen = false;
			goto(`${origin}/content/published`);
		} else {
			toastMessages.danger($_('common.error.sorry'), resp.error);
		}
	};
	const showSchedule = () => {
		if (isMember) return false;
		return $post.status == undefined || $post.status == 'draft' || $post.status == 'scheduled';
	};
</script>

<footer>
	<div>
		{#if showPreviewImg}
			<PreviewImageSelector {post} />
		{/if}
		<CollectionSelector
			bind:selectedCollection={$post.collection}
			bind:selectedCollectionId={$post.collection_id}
			bind:hideFromAll={$post.hide_all} />
		<TagInput bind:addedTags={$post.tags} />
	</div>
	<div>
		{#if $post.status == undefined && !isWidgetPost}
			<Button displayType="secondary" on:click={() => save({ asDraft: true })}>
				{$_('compose.save.draft')}
			</Button>
		{/if}
		{#if showSchedule()}
			<ScheduleTimeDatePicker bind:date={scheduleTime} bind:isScheduled={shouldBeScheduled} />
		{/if}
		{#if $post.status == 'draft'}
			<Button displayType="primary" on:click={() => save({ asDraft: true })}
				>{$_('compose.update')}
			</Button>
		{:else if $post.status == 'scheduled'}
			<Button on:click={unschedule} displayType="secondary">{$_('post.status.unschedule')}</Button>
		{/if}
		<Button on:click={() => save({})} displayType="primary">
			{getSubmitButtonLabel()}
		</Button>
	</div>
</footer>

<style>
	footer {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		inline-size: 100%;
		padding: var(--gutterSmall);
		border-block-start: solid 1px var(--grayLight);
	}

	div {
		display: flex;
		justify-content: center;
		margin-block-end: var(--gutterSmall);
		gap: var(--gutterSmall);
	}

	@media (max-width: 1024px) {
		footer {
			justify-content: center;
		}
	}
</style>
